<template>
  <b-form id="product-create-form" @submit.stop.prevent>
    <b-form-group id="input-group-2">
      <b-row>
        <b-col cols="12" md="9" class="mb-3 mb-md-0">
          <div class="products-modal-label">{{ $gettext('Amount*') }}</div>
          <b-input-group class="product-amount-group">
            <b-form-input id="input-2" type="number" v-model="item.amount" class="product-amount-input" required></b-form-input>
            <b-form-select id="input-2" v-model="item.amount_is_gross" :options="priceOptions" class="product-amount-select" required></b-form-select>
          </b-input-group>
        </b-col>

        <b-col cols="12" md="3">
          <div class="products-modal-label">{{ $gettext('Tax rate*') }}</div>
          <VatInput v-model="item" countryCode="currentCompany.country" />
        </b-col>
      </b-row>
    </b-form-group>

    <b-form-group id="input-group-4">
      <div class="products-modal-label">{{ $gettext('Group') }}</div>
      <b-form-input v-if="showCreateGroupInput" id="input-4" v-model="newGroup.name" required></b-form-input>
      <b-form-select v-else id="input-4" v-model="item.group" :options="groupOptions" value-field="id" text-field="name"></b-form-select>
      <div v-if="allowGroupCreation" class="d-flex justify-content-end">
        <GekkoButton 
          v-if="!showCreateGroupInput"
          :icon="'fa-solid fa-plus'"
          @click="showCreateGroupInput = true"
          :text="$gettext('Create new')"
          variant="link"
          class="text-blue"
        />
        <GekkoButton 
          v-else
          @click="showCreateGroupInput = false"
          :icon="'fa-solid fa-arrow-left'"
          :text="$gettext('Add existing')"
          variant="link"
          class="text-blue"
        />
      </div>
    </b-form-group>
    
    <b-form-group id="input-group-1">
      <div class="products-modal-label">{{ $gettext('Description') }}</div>
      <b-form-textarea id="input-1" v-model="item.description" :placeholder="$gettext('Product description...')" rows="3" no-resize required />
    </b-form-group>
  </b-form>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import ProductGroupService from '@/store/services/productgroup.service.js'
import ProductService from '@/store/services/product.service.js'

import VatInput from '@/components/VatInput.vue'
import GekkoButton from '@/components/GekkoButton'

export default {
  name: 'AddEditProduct',
  components: {
    VatInput,
    GekkoButton,
  },
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    initialGroupId: {
      type: Number,
      default: null,
    },
    allowGroupCreation: {
      type: Boolean,
      default: false,
    },
    productName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      item: {},
      groupOptions: [],
      newGroup: {},
      showCreateGroupInput: false,
      priceOptions: [
        { value: false, text: this.$gettext('Price (ex. VAT)') },
        { value: true, text: this.$gettext('Price (incl. VAT)') },
      ],
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: 'main/getCurrentCompany',
      isWaitingForResponse: 'main/isWaitingForResponse',
    }),
    isEdit() {
      return this.product?.id
    },
  },
  created() {
    this.setupData()
  },
  methods: {
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE',
    }),
    setupData() {
      if (this.initialGroupId) {
        this.group = this.initialGroupId
      }
      this.getProductGroups()
      if (this.product && this.product.id) {
        this.item = Object.assign({}, this.product, {})
        if (this.item.amount_is_gross) {
          this.item.amount = this.item.total_inc_vat
        }
      } else {
        this.item = {
          amount: null,
          description: this.productName ? this.productName : null,
          amount_is_gross: false,
          archived: false,
          company_profile: this.currentCompany.id,
          total_inc_vat: false,
          vat_exempt: false,
          group: null,
        }
        if (this.initialGroupId) {
          this.item.group = this.initialGroupId
        }
        if (!this.currentCompany.default_tax_rate_exempt) {
          this.item.tax_rate = this.currentCompany.default_tax_rate
        } else {
          this.item.tax_rate = 21
          this.item.vat_exempt = true
        }
        if (this.allowGroupCreation) {
          this.newGroup.name = null
        }
      }
    },
    getProductGroups() {
      return ProductGroupService.list({ company_profile: this.currentCompany.id }).then((response) => {
        this.groupOptions = response.data
        this.groupOptions.unshift({ id: null, name: '---' })
      })
    },
    save() {
      if (!this.currentCompany.is_premium) {
        this.$store.commit('togglePremiumModal', 'web_product_add')
        return
      }

      if (this.isEdit) {
        this.saveEdit()
      } else {
        this.saveCreate()
      }
    },
    saveCreate() {
      if (this.allowGroupCreation && this.showCreateGroupInput) {
        this.setWaitingForResponse(true)
        ProductGroupService.create({ company_profile: this.currentCompany.id, ...this.newGroup })
          .then((response) => {
            this.item.group = response.data.id
            this.createProduct(true)
          })
          .catch((response) => {
            if (response.data['non_field_errors']) {
              this.showAlert('group')
            }
            this.setWaitingForResponse(false)
          })
      } else {
        this.setWaitingForResponse(true)
        if (!this.item.group) {
          delete this.item.group
        }
        this.createProduct(false)
      }
    },
    saveEdit() {
      this.setWaitingForResponse(true)
      ProductService.update(this.product.id, this.item)
        .then(() => {
          this.$emit('product-updated', this.product.id)
        })
        .catch((response) => {
          if (response.data['non_field_errors']) {
            this.showAlert()
          }
        })
        .finally(() => {
          this.setWaitingForResponse(false)
        })
    },
    createProduct(withGroup = false) {
      ProductService.create(this.item)
        .then((response) => {
          this.$emit('product-created', response.data)
          if (withGroup) {
            this.$emit('group-created')
          }
        })
        .catch((response) => {
          if (withGroup) {
            this.getProductGroups().then(() => {
              this.showCreateGroupInput = false
            })
          }

          if (response.data['non_field_errors']) {
            this.showAlert()
          } else if (response.data['amount']) {
            this.showAlert('amount')
          } else {
            this.showAlert('general')
          }
        })
        .finally(() => {
          this.setWaitingForResponse(false)
        })
    },
    showAlert(type = 'product') {
      let msg = this.$gettext('A product with this name already exists.')
      if (type === 'group') {
        msg = this.$gettext('A group with this name already exists.')
      } else if (type === 'amount') {
        msg = this.$gettext('Please enter a valid amount.')
      } else if (type === 'general') {
        msg = this.$gettext('An error occurred while saving your product. Please check the details and try again.')
      }
      this.$store.commit('setAlert', {
        title: this.$gettext('Could not save'),
        text: msg,
        variant: 'warning',
        duration: 2,
      })
    },
  },
}
</script>

<style scoped>
.products-modal-label {
  font-size: 12px;
  color: grey;
  display: block;
}

.product-amount-group {
  width: 100%;
}

.product-amount-input {
  flex: 0 0 65%;
  max-width: 65%;
}

.product-amount-select {
  flex: 0 0 35%;
  max-width: 35%;
}
</style>